// src/views/admin/main/others/messages/components/SingleChat.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "contexts/authContext";
import { useChat } from "hooks/useChat";
import * as chatApi from "api";
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import md5 from 'md5';

import Card from "components/card";
import CardMenu from 'components/card/CardMenu';
import Modal from "components/modal/Modal";
import SuccessAlert from "components/alerts/SuccessAlerts";
import ErrorAlerts from "components/alerts/ErrorAlerts";
import SingleMessage from "./SingleMessage";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { renderTrackMessages, renderThumbMessages, renderViewMessages } from "components/scrollbar/Scrollbar";
import Loader from 'components/loaders/Loader';

import { MdAdd, MdDelete, MdChevronLeft, MdImage, MdDescription, MdOutlineAttachment, MdOutlineTagFaces, MdClose } from "react-icons/md";
import { IoPaperPlane, IoInformationCircleOutline } from "react-icons/io5";
import { FaUsers, FaCube } from "react-icons/fa";
import { BiSearch } from "react-icons/bi";

// Helper component for the subtle wave pattern background
const ChatBackground = () => (
    <div className="absolute inset-0 z-0 overflow-hidden opacity-10 dark:opacity-20">
        <svg
            className="absolute inset-0 h-full w-full"
            fill="none"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M0 50 C 25 30, 75 70, 100 50" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 60 C 25 40, 75 80, 100 60" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 40 C 25 20, 75 60, 100 40" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 70 C 25 50, 75 90, 100 70" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 30 C 25 10, 75 50, 100 30" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 20 C 25 0, 75 40, 100 20" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 80 C 25 60, 75 100, 100 80" stroke="currentColor" strokeWidth="0.1"/>
            <path d="M0 90 C 25 70, 75 110, 100 90" stroke="currentColor" strokeWidth="0.1"/>
        </svg>
    </div>
);


const formatDateSeparator = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
};

const getGravatarUrl = (email) => {
    if (!email) return 'https://www.gravatar.com/avatar/?d=mp';
    const hash = md5(email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
};

// Utility function to construct proper image URL
const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith("http")) {
        return imageUrl;
    }
    
    const serverUrl = process.env.REACT_APP_API_BASE_URL;
    if (serverUrl) {
        return `${serverUrl.replace(/\/$/, "")}/${imageUrl.replace(/^\/uploads/, "uploads/images")}`;
    }
    
    return imageUrl;
};

const SingleChat = ({ chat, onClose, isMobile }) => {
    const { user: currentUser } = useAuth();
    const { sendMessage, typingUsers, uploadImage, uploadDocument, uploadModel, startTyping, stopTyping } = useChat(currentUser);
    const queryClient = useQueryClient();

    const [newMessage, setNewMessage] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [replyingToMessage, setReplyingToMessage] = useState(null);
    const [showUploadMenu, setShowUploadMenu] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showClearChatModal, setShowClearChatModal] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [componentError, setComponentError] = useState(null);
    const [isTyping, setIsTyping] = useState(false);

    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const uploadMenuRef = useRef(null); // Ref for the upload menu
    const emojiPickerRef = useRef(null); // Ref for the emoji picker

    const isTeamChat = chat?.type === 'team';
    const chatPartnerId = chat?.participant?.id;
    const chatId = chat?.id;

    // Click-outside logic for popups
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (uploadMenuRef.current && !uploadMenuRef.current.contains(event.target)) {
                setShowUploadMenu(false);
            }
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!chat) return;

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        if (newMessage.length > 0) {
            if (!isTyping) {
                const payload = isTeamChat ? { teamId: chatId } : { receiverId: chatPartnerId };
                startTyping(payload);
                setIsTyping(true);
            }
            typingTimeoutRef.current = setTimeout(() => {
                const payload = isTeamChat ? { teamId: chatId } : { receiverId: chatPartnerId };
                stopTyping(payload);
                setIsTyping(false);
            }, 1500);
        } else if (isTyping) {
            const payload = isTeamChat ? { teamId: chatId } : { receiverId: chatPartnerId };
            stopTyping(payload);
            setIsTyping(false);
        }

        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, [newMessage, chat, isTeamChat, chatId, chatPartnerId, isTyping, startTyping, stopTyping]);

    const { data: fullChatPartner, isLoading: isChatPartnerLoading } = useQuery({
        queryKey: ['chatPartner', chatPartnerId],
        queryFn: () => chatApi.fetchUserById(chatPartnerId),
        enabled: !!chatPartnerId && !isTeamChat,
    });
    
    const chatPartner = useMemo(() => {
        if (!chat) return null;
        if (isTeamChat) {
            return chat.team;
        }
        let partnerData = fullChatPartner || chat.participant;
        if (partnerData && partnerData.user) {
            partnerData = partnerData.user;
        }
        return partnerData;
    }, [chat, isTeamChat, fullChatPartner]);
    
    const queryKey = useMemo(() => chat?.isNew ? null : (isTeamChat ? ['messages', 'team', chatId] : ['messages', 'user', chatPartnerId]), [chat, isTeamChat, chatId, chatPartnerId]);
    
    const fetchFn = () => isTeamChat ? chatApi.getMessagesForTeam(chatId) : chatApi.getMessagesForUser(chatPartnerId);
    const { data: messages = [], isLoading: isMessagesLoading } = useQuery({ queryKey, queryFn: fetchFn, enabled: !!chatId });

    const filteredMessages = useMemo(() => {
        if (!searchTerm) return messages;
        return messages.filter(msg => msg.content?.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [messages, searchTerm]);

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollToBottom(); }, [filteredMessages]);
    
    useEffect(() => {
        if (!currentUser || !chat || !messages.length) return;
    
        const unreadMessageIds = messages
            .filter(msg => {
                if (isTeamChat) {
                    // For team chats, messages are unread if they were not sent by the current user.
                    return !msg.isRead && msg.sender.id !== currentUser.id;
                }
                // For individual chats, messages are unread if the receiver is the current user.
                return !msg.isRead && msg.receiverId === currentUser.id;
            })
            .map(msg => msg.id);
    
        if (unreadMessageIds.length > 0) {
            if (isTeamChat) {
                // Call the correct API function for team chats
                chatApi.markTeamMessagesAsRead({ messageIds: unreadMessageIds, teamId: chatId });
            } else {
                // Call the correct API function for individual chats
                chatApi.markMessagesAsRead({ messageIds: unreadMessageIds, chatPartnerId: chatPartnerId });
            }
        }
    }, [messages, currentUser, isTeamChat, chatId, chatPartnerId, chat]);

    useEffect(() => {
        if (isTeamChat) {
            if (!chat.team?.name) {
                setComponentError("Team chat data is incomplete: 'name' is missing.");
            }
        } else {
            if (!chatPartner?.firstName || !chatPartner?.lastName) {
                setComponentError("Individual chat data is incomplete: 'firstName' or 'lastName' is missing.");
            }
        }
    }, [chat, chatPartner, isTeamChat]);

    const onEmojiClick = (emojiObject) => { setNewMessage(prev => prev + emojiObject.emoji); setShowEmojiPicker(false); };

    const handleFileSelection = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        const fileName = file.name.toLowerCase();
        
        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png') || fileName.endsWith('.gif')) {
            setFileType('image');
            setFilePreviewUrl(URL.createObjectURL(file));
        } else if (fileName.endsWith('.pdf') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
            setFileType('document');
            setFilePreviewUrl(null);
        } else if (fileName.endsWith('.stl') || fileName.endsWith('.obj')) {
            setFileType('model');
            setFilePreviewUrl(null);
        } else {
            console.log(`Unsupported file type detected: ${file.type} or extension not found.`);
            setFileType(null);
            setFilePreviewUrl(null);
        }
    };

    const handleUploadClick = (type) => {
        if (!fileInputRef.current) return;
        if (type === 'image') {
            fileInputRef.current.accept = 'image/*';
        } else if (type === 'document') {
            fileInputRef.current.accept = '.pdf,.doc,.docx';
        } else if (type === 'model') {
            fileInputRef.current.accept = '.stl,.obj';
        }
        fileInputRef.current.click();
        setShowUploadMenu(false);
    };

    const handleSendMessage = () => {
        if (!chat) return;
        const trimmedMessage = newMessage.trim();
        const isSendingFile = selectedFile !== null;

        if (!trimmedMessage && !isSendingFile) {
            return;
        }

        const isTeam = isTeamChat;
        const chatID = chat.id;

        if (isSendingFile) {
            const formData = new FormData();
            formData.append('file', selectedFile);
            if (trimmedMessage) {
                formData.append('content', trimmedMessage);
            }
            if (replyingToMessage) {
                formData.append('repliedToMessageId', replyingToMessage.id);
            }
            if (isTeam) {
                formData.append('teamId', chatID);
            } else {
                formData.append('receiverId', chatID);
            }

            switch (fileType) {
                case 'image':
                    uploadImage.mutate({ formData, isTeamChat: isTeam, chatId: chatID });
                    break;
                case 'document':
                    uploadDocument.mutate({ formData, isTeamChat: isTeam, chatId: chatID });
                    break;
                case 'model':
                    uploadModel.mutate({ formData, isTeamChat: isTeam, chatId: chatID });
                    break;
                default:
                    break;
            }
        } else {
            const messageData = {
                content: trimmedMessage,
                repliedToMessageId: replyingToMessage?.id || null,
            };

            if (isTeam) {
                messageData.teamId = chatID;
            } else {
                messageData.receiverId = chatID;
            }

            sendMessage.mutate({ isTeamChat: isTeam, messageData });
        }

        setNewMessage("");
        setSelectedFile(null);
        setFilePreviewUrl(null);
        setReplyingToMessage(null);
        setFileType(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    
    const confirmClearChat = useMutation({
        mutationFn: () => isTeamChat ? chatApi.clearTeamChatHistory(chat.id) : chatApi.clearChatHistory(chat.participant?.id || chat.id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey});
            setShowClearChatModal(false);
            setShowSuccessAlert(true);
            setTimeout(() => setShowSuccessAlert(false), 3000);
        },
        onError: (error) => {
            setErrorMessages([error.message || "Failed to clear chat."]);
            setShowErrorAlert(true);
            setTimeout(() => setShowErrorAlert(false), 5000);
        }
    });

    const chatName = isTeamChat ? chat.team?.name : `${chatPartner?.firstName || 'Unknown'} ${chatPartner?.lastName || 'User'}`;
    const chatImageUrl = isTeamChat ? null : (getImageUrl(chatPartner?.imageUrl) || getGravatarUrl(chatPartner?.email));
    
    const partnerIdForTyping = isTeamChat ? chatId : chatPartnerId;
    const isPartnerTyping = typingUsers[partnerIdForTyping];
    
    const statusText = useMemo(() => {
        if (isPartnerTyping) {
            if (isTeamChat) {
                 return `${typingUsers[chat.id] || 'A teammate'} is typing...`;
            }
            return "typing...";
        }
        if (isTeamChat) {
            return `${chat.team?.members?.length || 0} members`;
        }
        return chatPartner?.status || 'Offline';
    }, [isPartnerTyping, isTeamChat, chat, chatPartner, typingUsers]);

    if (componentError) {
        return (
            <Card extra={"w-full h-full p-4 flex flex-col items-center justify-center bg-red-100 dark:bg-red-800"}>
                <h2 className="text-2xl font-bold text-red-700 dark:text-white">Error Loading Chat</h2>
                <p className="mt-4 text-center text-sm text-red-600 dark:text-red-200">{componentError}</p>
                <button onClick={onClose} className="mt-6 rounded-lg bg-red-500 px-4 py-2 text-white font-semibold">Close Chat</button>
            </Card>
        );
    }
    
    if (!chat || (!!chatPartnerId && isChatPartnerLoading)) {
        return <Loader />;
    }

    return (
        <Card extra={"w-full h-full p-0 flex flex-col relative overflow-hidden bg-white dark:bg-navy-800"}>
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-navy-700">
                <div className="flex items-center gap-4">
                    {isMobile && <button onClick={onClose} className="text-gray-600 dark:text-gray-300 hover:text-navy-700"><MdChevronLeft size={24} /></button>}
                    <div className="relative h-12 w-12 flex-shrink-0">
                        {chatImageUrl ? (
                            <img 
                                src={chatImageUrl} 
                                alt={chatName} 
                                className="h-full w-full rounded-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-200 dark:bg-navy-700" style={{ display: chatImageUrl ? 'none' : 'flex' }}>
                            <FaUsers className="h-6 w-6 text-gray-500" />
                        </div>
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-navy-700 dark:text-white">{chatName}</h1>
                        <p className={`text-sm ${isPartnerTyping || statusText === 'Online' ? 'text-green-500' : 'text-gray-500'}`}>{statusText}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-40 hidden md:flex">
                        <BiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Search messages..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full h-10 rounded-full bg-gray-100 pl-10 pr-3 text-sm text-navy-700 outline-none dark:bg-navy-900 dark:text-white" />
                    </div>
                    {/* FIX: Give CardMenu a high z-index to pop over messages */}
                    <div className="relative z-20">
                        <CardMenu vertical menuItems={[{ label: "Clear Chat", icon: <MdDelete />, onClick: () => setShowClearChatModal(true) }]} />
                    </div>
                </div>
            </div>

            {currentUser.role === 'mentor' && !isTeamChat && chatPartner?.team && (
                <div className="flex-shrink-0 flex items-center gap-2 bg-blue-50 p-2 text-sm text-blue-800 dark:bg-blue-500/20 dark:text-blue-300">
                    <IoInformationCircleOutline className="h-5 w-5" />
                    <span>You are conversing with <strong>{chatPartner.firstName}</strong> from Team <strong>{chatPartner.team.name}</strong>.</span>
                </div>
            )}
            
            <div className="relative flex-grow overflow-y-auto bg-gray-100 dark:bg-navy-900 border-x border-gray-200 dark:border-navy-700">
                <ChatBackground />
                <Scrollbars ref={scrollRef} renderTrackVertical={renderTrackMessages} renderThumbVertical={renderThumbMessages} renderView={renderViewMessages} autoHide>
                    <div className="p-4 relative z-10">
                        {isMessagesLoading ? <Loader /> : filteredMessages.map((msg, index) => {
                                 const prevMsg = filteredMessages[index - 1];
                                 const nextMsg = filteredMessages[index + 1];
                                 const showAvatar = !prevMsg || prevMsg.sender.id !== msg.sender.id || (new Date(msg.timestamp).toDateString() !== new Date(prevMsg.timestamp).toDateString());
                                 const showDateSeparator = !prevMsg || new Date(msg.timestamp).toDateString() !== new Date(prevMsg.timestamp).toDateString();
                                 const showTimestamp = !nextMsg || nextMsg.sender.id !== msg.sender.id || (new Date(nextMsg.timestamp).getTime() - new Date(msg.timestamp).getTime() > 60000);
                                 return (
                                     <React.Fragment key={msg.id}>
                                         {showDateSeparator && (
                                             <div className="my-4 text-center text-xs font-semibold text-gray-500">
                                                 <span className="rounded-full bg-white px-3 py-1 shadow-sm dark:bg-navy-700">{formatDateSeparator(msg.timestamp)}</span>
                                             </div>
                                         )}
                                         <SingleMessage message={msg} showAvatar={showAvatar} onReply={setReplyingToMessage} isTeamChat={isTeamChat} showTimestamp={showTimestamp} />
                                     </React.Fragment>
                                 );
                        })}
                        {chat?.isNew && !isMessagesLoading &&
                            <div className="py-10 text-center text-gray-400">
                                <p>This is the beginning of your conversation with {chatName}.</p>
                            </div>
                        }
                    </div>
                </Scrollbars>
            </div>

            <div className="flex-shrink-0 border-t border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
                {replyingToMessage && (
                    <div className="relative mb-2 rounded-t-lg bg-gray-100 p-2 dark:bg-navy-700">
                        <div className="border-l-4 border-brand-500 pl-3">
                            <p className="font-bold text-brand-500">Replying to {replyingToMessage.sender.firstName}</p>
                            <p className="truncate text-sm text-gray-600 dark:text-gray-300">{replyingToMessage.content}</p>
                        </div>
                        <MdClose onClick={() => setReplyingToMessage(null)} className="absolute top-2 right-2 h-5 w-5 cursor-pointer text-gray-500" />
                    </div>
                )}
                {selectedFile && (
                    <div className="relative mb-2 flex items-center gap-3 rounded-lg bg-gray-100 p-2 dark:bg-navy-700">
                        {filePreviewUrl ? <img src={filePreviewUrl} alt="preview" className="h-12 w-12 rounded-lg object-cover" /> : <MdOutlineAttachment className="h-12 w-12 rounded-lg bg-gray-200 p-2 dark:bg-navy-800" />}
                        <span className="truncate text-sm font-medium text-navy-700 dark:text-white">{selectedFile.name}</span>
                        <MdClose onClick={() => {setSelectedFile(null); setFilePreviewUrl(null); if(fileInputRef.current) fileInputRef.current.value = "";}} className="absolute top-2 right-2 h-5 w-5 cursor-pointer text-gray-500 hover:text-red-500" />
                    </div>
                )}
                <div className="relative flex w-full items-center gap-2">
                    <div className="relative z-50">
                        <button onClick={() => setShowUploadMenu(p => !p)} className="rounded-full p-3 transition-colors hover:bg-gray-100 dark:hover:bg-navy-700">
                            <MdAdd className={`h-6 w-6 text-gray-500 transition-transform ${showUploadMenu ? 'rotate-45' : ''}`} />
                        </button>
                        {showUploadMenu && (
                            <div className="absolute bottom-full mb-2 w-48 rounded-lg bg-white p-2 shadow-xl dark:bg-navy-700">
                                <div onClick={() => handleUploadClick('image')} className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-100 dark:hover:bg-navy-600">
                                    <MdImage className="h-5 w-5 text-orange-500" /> <span>Image</span>
                                </div>
                                <div onClick={() => handleUploadClick('document')} className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-100 dark:hover:bg-navy-600">
                                    <MdDescription className="h-5 w-5 text-orange-500" /> <span>Document</span>
                                </div>
                                <div onClick={() => handleUploadClick('model')} className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-100 dark:hover:bg-navy-600">
                                    <FaCube className="h-5 w-5 text-orange-500" /> <span>3D Model</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handleFileSelection} className="hidden" />
                    <div className="relative z-50">
                        <button onClick={() => setShowEmojiPicker(p => !p)} className="rounded-full p-3 transition-colors hover:bg-gray-100 dark:hover:bg-navy-700">
                            <MdOutlineTagFaces className="h-6 w-6 text-gray-500" />
                        </button>
                        {showEmojiPicker && (
                            <div className="absolute bottom-full mb-2 right-0 sm:right-auto sm:left-0 z-10"><EmojiPicker onEmojiClick={onEmojiClick} autoFocusSearch={false} emojiStyle={EmojiStyle.NATIVE} /></div>
                        )}
                    </div>
                    <div className="relative flex-grow">
                        <input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }} placeholder="Write a message..." className="w-full rounded-full bg-gray-100 py-3 pl-5 pr-14 text-navy-700 outline-none dark:bg-navy-900 dark:text-white" />
                        <button onClick={handleSendMessage} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-brand-500 p-2 text-white transition-transform hover:scale-110 disabled:scale-100 disabled:bg-gray-400" disabled={!newMessage.trim() && !selectedFile}>
                            <IoPaperPlane />
                        </button>
                    </div>
                </div>
            </div>
            {showClearChatModal && (
                <Modal onClose={() => setShowClearChatModal(false)}>
                    <div className="p-6 text-center">
                        <h2 className="text-lg font-bold">Clear Chat History?</h2>
                        <p className="mt-2 text-gray-600">This action cannot be undone.</p>
                        <div className="mt-4 flex justify-center gap-4">
                            <button onClick={() => setShowClearChatModal(false)} className="rounded-md bg-gray-200 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-300">Cancel</button>
                            <button onClick={() => confirmClearChat.mutate()} className="rounded-md bg-red-500 px-4 py-2 font-semibold text-white hover:bg-red-600">Clear</button>
                        </div>
                    </div>
                </Modal>
            )}
            {showSuccessAlert && <SuccessAlert message="Chat cleared successfully!" onClose={() => setShowSuccessAlert(false)} />}
            {showErrorAlert && <ErrorAlerts errors={errorMessages} onClose={() => setShowErrorAlert(false)} />}
        </Card>
    );
};

export default SingleChat;